import {
  Alert,
  alertClasses,
  ErrorOutlineOutlinedIcon,
  NumberInput,
  Popper,
  type FormNumberInputProps,
} from '@shared/components';
import { useEffect, useRef, useState } from 'react';
import { useController, type FieldValues } from 'react-hook-form';

export default function FormNumberInputWithAlertError<
  TFieldValues extends FieldValues = FieldValues,
>(props: Readonly<FormNumberInputProps<TFieldValues>>) {
  const { ...restProps } = props;
  const inputRef = useRef<HTMLDivElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [isInputShowingOnScreen, setIsInputShowingOnScreen] = useState(false);

  const {
    field,
    fieldState: { error },
  } = useController({ ...props, name: props.name });

  const isError = !!error;

  useEffect(() => {
    if (isError && inputRef.current) {
      setAnchorEl(inputRef.current);
    } else {
      setAnchorEl(null);
    }
  }, [error]);

  useEffect(() => {
    const dialogContentElement = document.querySelector('.MuiDialogContent-root');

    const handleScroll = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (rect && dialogContentElement) {
        const isVisibleArea =
          rect.bottom <= dialogContentElement.scrollTop + dialogContentElement.clientHeight;
        setIsInputShowingOnScreen(isVisibleArea);
      }
    };

    if (dialogContentElement) {
      dialogContentElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (dialogContentElement) {
        dialogContentElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <>
      <NumberInput
        {...field}
        ref={inputRef}
        {...restProps}
        autoComplete='off'
        error={isError}
        required={Boolean(props.rules?.required ?? props.required)}
        slotProps={{
          input: {
            sx: {
              '> input': {
                minHeight: '32px',
                textAlign: 'center',
              },
            },
          },
        }}
      />
      <Popper
        open={isError && isInputShowingOnScreen}
        anchorEl={anchorEl}
        placement='bottom'
        sx={(theme) => ({
          zIndex: theme.zIndex.modal,
        })}
      >
        <Alert
          ref={alertRef}
          severity='error'
          variant='outlined'
          iconMapping={{
            error: <ErrorOutlineOutlinedIcon sx={{ fontSize: '16px' }} />,
          }}
          sx={(theme) => ({
            mt: theme.spacing(1),
            p: theme.spacing(2, 3),
            zIndex: theme.zIndex.tooltip,
            gap: theme.spacing(1),
            alignItems: 'center',
            ...theme.typography.captionSemibold,
            [`& .${alertClasses.icon}`]: {
              fontSize: '16px',
              mr: theme.spacing(0),
            },
          })}
        >
          {!error?.message ? 'เกิดข้อผิดพลาด' : error.message}
        </Alert>
      </Popper>
    </>
  );
}
